import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Ban, CheckCircle, Plus, Shield, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";
import { useCreateDNSPolicy } from "@/hooks/useDNSPolicies";
import { useUserOrganization } from "@/hooks/useOrganization";

interface DomainManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: 'block' | 'whitelist' | 'policy';
  initialDomain?: string;
}

export function DomainManagementModal({
  isOpen,
  onClose,
  action,
  initialDomain = ''
}: DomainManagementModalProps) {
  const { data: organization } = useUserOrganization();
  const createPolicy = useCreateDNSPolicy();

  const [domain, setDomain] = useState(initialDomain);
  const [policyName, setPolicyName] = useState('');
  const [policyDescription, setPolicyDescription] = useState('');

  const getActionConfig = () => {
    switch (action) {
      case 'block':
        return {
          title: 'Block Domain',
          description: 'Block access to this domain across your organization',
          icon: Ban,
          color: 'text-red-600',
          bgColor: 'bg-red-50 dark:bg-red-950'
        };
      case 'whitelist':
        return {
          title: 'Whitelist Domain',
          description: 'Allow access to this domain even if it matches blocking rules',
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50 dark:bg-green-950'
        };
      case 'policy':
        return {
          title: 'Create Policy',
          description: 'Create a new DNS filtering policy for this domain',
          icon: Plus,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50 dark:bg-blue-950'
        };
      default:
        return {
          title: 'Domain Management',
          description: 'Manage domain access and policies',
          icon: Shield,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50 dark:bg-gray-950'
        };
    }
  };

  const config = getActionConfig();
  const IconComponent = config.icon;

  const handleSubmit = async () => {
    if (!domain.trim()) {
      toast.error("Please enter a domain name");
      return;
    }

    if (!organization) {
      toast.error("Please set up your organization first");
      return;
    }

    try {
      switch (action) {
        case 'block':
          await createPolicy.mutateAsync({
            name: `Block ${domain.trim()}`,
            description: `Blocks access to ${domain.trim()}`,
            organization_id: organization.id,
            block_social_media: false,
            block_gaming: false,
            block_streaming: false,
            block_news: false,
            block_adult_content: false,
            allow_bypass_requests: false,
            is_active: true,
            custom_domains: domain.trim(),
          });
          toast.success(`Domain ${domain.trim()} has been blocked successfully`);
          break;

        case 'whitelist':
          await createPolicy.mutateAsync({
            name: `Allow ${domain.trim()}`,
            description: `Allows access to ${domain.trim()}`,
            organization_id: organization.id,
            block_social_media: false,
            block_gaming: false,
            block_streaming: false,
            block_news: false,
            block_adult_content: false,
            allow_bypass_requests: false,
            is_active: true,
            custom_domains: domain.trim(),
            domain_action: 'allow' as const,
          });
          toast.success(`Domain ${domain.trim()} has been whitelisted successfully`);
          break;

        case 'policy':
          if (!policyName.trim()) {
            toast.error("Please enter a policy name");
            return;
          }
          await createPolicy.mutateAsync({
            name: policyName,
            description: policyDescription,
            organization_id: organization.id,
            block_social_media: false,
            block_gaming: false,
            block_streaming: false,
            block_news: false,
            block_adult_content: false,
            allow_bypass_requests: false,
            is_active: true,
            custom_domains: domain.trim(),
          });
          toast.success(`Policy "${policyName}" has been created successfully`);
          break;
      }

      onClose();
      resetForm();
    } catch (error) {
      console.error('Action failed:', error);
      toast.error("An error occurred. Please try again.");
    }
  };

  const resetForm = () => {
    setDomain('');
    setPolicyName('');
    setPolicyDescription('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isLoading = createPolicy.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${config.bgColor}`}>
              <IconComponent className={`h-6 w-6 ${config.color}`} />
            </div>
            <div>
              <DialogTitle>{config.title}</DialogTitle>
              <DialogDescription>{config.description}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="domain">Domain Name</Label>
            <Input
              id="domain"
              placeholder="Enter domain (e.g., example.com)"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="mt-2"
            />
          </div>

          {action === 'policy' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="policyName">Policy Name</Label>
                <Input
                  id="policyName"
                  placeholder="Enter policy name"
                  value={policyName}
                  onChange={(e) => setPolicyName(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="policyDescription">Description</Label>
                <Textarea
                  id="policyDescription"
                  placeholder="Describe what this policy does"
                  value={policyDescription}
                  onChange={(e) => setPolicyDescription(e.target.value)}
                  rows={3}
                  className="mt-2"
                />
              </div>
            </div>
          )}

          {action === 'block' && (
            <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-yellow-800 dark:text-yellow-200">
                  Blocking this domain will prevent all users in your organization from accessing it.
                </p>
                <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                  Make sure this is the intended action before proceeding.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !domain.trim()}
            className={action === 'block' ? 'bg-red-600 hover:bg-red-700' : ''}
          >
            {isLoading ? (
              <Clock className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <IconComponent className="h-4 w-4 mr-2" />
            )}
            {action === 'block' ? 'Block Domain' :
             action === 'whitelist' ? 'Whitelist Domain' :
             'Create Policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
